import React, { useEffect, useMemo, useState } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import { getProfileTypeLabel } from "../Watchlists/profileTypeCatalog.js";
import { CancelButton, SaveButton, ActionButtonsRow } from "../Watchlists/ActionButtons.js";

const WATCHLISTS_URL = "/umbraco/api/WatchlistsApi/Watchlists";
const PROFILE_TYPES_URL = "/umbraco/api/WatchlistsApi/WatchlistProfileTypes";
const WATCHLIST_ITEMS_URL = "/umbraco/api/WatchlistsApi/WatchlistItems";
const CONTAINING_URL = "/umbraco/api/WatchlistsApi/WatchlistsContainingProfile";

const readErrorMessage = async (response, fallback) => {
    const message = await response.text();
    return message || fallback;
};

const ensureProfileTypeEnabled = async (workspaceId, profileType, existingTypes = []) => {
    const types = existingTypes || [];
    if (types.includes(profileType))
        return;

    const formData = new FormData();
    formData.append("workspaceId", workspaceId);
    formData.append("profileType", profileType);

    const response = await fetch(PROFILE_TYPES_URL, { method: "POST", body: formData });
    if (!response.ok)
        throw new Error(await readErrorMessage(response, "Could not enable profile type on watchlist"));
};

const addItemToWatchlist = async ({ workspaceId, profileType, uniqueId, slug }) => {
    const formData = new FormData();
    formData.append("type", profileType);
    formData.append("uniqueId", uniqueId);
    formData.append("slug", slug || uniqueId);
    formData.append("workspaceId", workspaceId);

    const response = await fetch(WATCHLIST_ITEMS_URL, { method: "POST", body: formData });
    if (!response.ok)
        throw new Error(await readErrorMessage(response, "Could not add item to watchlist"));
};

const AddToWatchlistPopup = ({
    isOpen,
    onClose,
    profileType,
    uniqueId,
    slug,
    profileName,
}) => {
    const [mode, setMode] = useState("select"); // select | create
    const [watchlists, setWatchlists] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [search, setSearch] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [errorName, setErrorName] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submittingWatchlistId, setSubmittingWatchlistId] = useState(null);
    const [containingWatchlistIds, setContainingWatchlistIds] = useState([]);

    const profileTypeLabel = getProfileTypeLabel(profileType);

    useEffect(() => {
        if (!isOpen)
            return;

        setMode("select");
        setSearch("");
        setName("");
        setDescription("");
        setErrorName(false);
        setErrorMessage(null);
        setSuccessMessage(null);
        setIsSubmitting(false);
        setSubmittingWatchlistId(null);
        setContainingWatchlistIds([]);

        setIsLoadingList(true);
        const containingPromise = (uniqueId && profileType)
            ? fetch(`${CONTAINING_URL}?type=${encodeURIComponent(profileType)}&uniqueId=${encodeURIComponent(uniqueId)}`)
                .then(async (response) => {
                    if (!response.ok)
                        return [];
                    const data = await response.json();
                    return Array.isArray(data) ? data.map((id) => Number(id)) : [];
                })
                .catch(() => [])
            : Promise.resolve([]);

        Promise.all([
            fetch(`${WATCHLISTS_URL}?returnAll=true`)
                .then(async (response) => {
                    if (!response.ok)
                        throw new Error(await readErrorMessage(response, "Could not load watchlists"));
                    return response.json();
                }),
            containingPromise,
        ])
            .then(([data, containingIds]) => {
                setWatchlists(Array.isArray(data) ? data : []);
                setContainingWatchlistIds(containingIds);
            })
            .catch((err) => setErrorMessage(err.message))
            .finally(() => setIsLoadingList(false));
    }, [isOpen, uniqueId, profileType]);

    const filteredWatchlists = useMemo(() => {
        const phrase = (search || "").trim().toLowerCase();
        if (!phrase)
            return watchlists;

        return watchlists.filter((w) => {
            const nameMatch = (w.Name || "").toLowerCase().includes(phrase);
            const descMatch = (w.Description || "").toLowerCase().includes(phrase);
            return nameMatch || descMatch;
        });
    }, [watchlists, search]);

    const finishSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => {
            onClose?.();
        }, 900);
    };

    const handleSelectWatchlist = async (watchlist) => {
        if (!uniqueId || !profileType || isSubmitting)
            return;

        setErrorMessage(null);
        setIsSubmitting(true);
        setSubmittingWatchlistId(watchlist.Id);

        try {
            await ensureProfileTypeEnabled(watchlist.Id, profileType, watchlist.ProfileTypes);
            await addItemToWatchlist({
                workspaceId: watchlist.Id,
                profileType,
                uniqueId,
                slug,
            });
            setContainingWatchlistIds((prev) =>
                prev.includes(Number(watchlist.Id)) ? prev : [...prev, Number(watchlist.Id)]
            );
            finishSuccess(`Added to “${watchlist.Name}”`);
        } catch (err) {
            setErrorMessage(err.message || "Could not add to watchlist");
            setIsSubmitting(false);
            setSubmittingWatchlistId(null);
        }
    };

    const handleCreateAndAdd = async () => {
        if (isSubmitting)
            return;

        setErrorMessage(null);
        setErrorName(false);

        if (!name || name.trim() === "") {
            setErrorName(true);
            return;
        }
        if (!uniqueId || !profileType) {
            setErrorMessage("Missing profile identifier");
            return;
        }

        setIsSubmitting(true);

        try {
            const createData = new FormData();
            createData.append("Name", name.trim());
            createData.append("Description", description || "");

            const createResponse = await fetch(WATCHLISTS_URL, { method: "POST", body: createData });
            if (!createResponse.ok)
                throw new Error(await readErrorMessage(createResponse, "Could not create watchlist"));

            const created = await createResponse.json();
            await ensureProfileTypeEnabled(created.Id, profileType, []);
            await addItemToWatchlist({
                workspaceId: created.Id,
                profileType,
                uniqueId,
                slug,
            });
            finishSuccess(`Created “${created.Name}” and added item`);
        } catch (err) {
            setErrorMessage(err.message || "Could not create watchlist");
            setIsSubmitting(false);
        }
    };

    if (!isOpen)
        return null;

    return (
        <div
            className="profiles-watchlist-popup-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSubmitting)
                    onClose?.();
            }}
        >
            <div className="add-task-popup profiles-watchlist-popup">
                <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start" }}>
                    <div style={{ width: "80%" }}>
                        <h3 style={{ margin: 0 }}>Add to watchlist</h3>
                        <div style={{ marginTop: "6px", color: "#64748b", fontSize: "13px" }}>
                            {profileName ? (
                                <>Adding <strong>{profileName}</strong> as {profileTypeLabel}</>
                            ) : (
                                <>As {profileTypeLabel}</>
                            )}
                        </div>
                    </div>
                    <div style={{ width: "20%", textAlign: "right" }}>
                        <button
                            type="button"
                            className="close-button"
                            onClick={() => !isSubmitting && onClose?.()}
                            disabled={isSubmitting}
                            aria-label="Close"
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>
                </div>

                <div className="profiles-watchlist-mode-tabs">
                    <button
                        type="button"
                        className={`profiles-watchlist-mode-tab${mode === "select" ? " active" : ""}`}
                        onClick={() => !isSubmitting && setMode("select")}
                        disabled={isSubmitting}
                    >
                        Existing watchlists
                    </button>
                    <button
                        type="button"
                        className={`profiles-watchlist-mode-tab${mode === "create" ? " active" : ""}`}
                        onClick={() => !isSubmitting && setMode("create")}
                        disabled={isSubmitting}
                    >
                        Create new
                    </button>
                </div>

                {errorMessage && (
                    <div style={{ color: "#e53e3e", marginBottom: "12px" }}>{errorMessage}</div>
                )}
                {successMessage && (
                    <div className="profiles-watchlist-success">{successMessage}</div>
                )}

                {mode === "select" && (
                    <div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search watchlists..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            disabled={isSubmitting || isLoadingList}
                            style={{ marginBottom: "12px" }}
                        />

                        {isLoadingList ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: "24px" }}>
                                <LoadingSpinnerImage />
                            </div>
                        ) : filteredWatchlists.length === 0 ? (
                            <div className="profiles-watchlist-empty">
                                {watchlists.length === 0
                                    ? "No watchlists yet. Create a new one."
                                    : "No watchlists match your search."}
                            </div>
                        ) : (
                            <div className="profiles-watchlist-list">
                                {filteredWatchlists.map((watchlist) => {
                                    const isRowBusy = submittingWatchlistId === watchlist.Id;
                                    const alreadyAdded = containingWatchlistIds.includes(Number(watchlist.Id));
                                    return (
                                        <div
                                            key={watchlist.Id}
                                            className="profiles-watchlist-list-item"
                                        >
                                            <div className="profiles-watchlist-list-item-main">
                                                <div className="profiles-watchlist-list-item-name">
                                                    {watchlist.Name}
                                                </div>
                                                {watchlist.Description && (
                                                    <div className="profiles-watchlist-list-item-desc">
                                                        {watchlist.Description}
                                                    </div>
                                                )}
                                                <div className="profiles-watchlist-list-item-meta">
                                                    {watchlist.ItemsCount ?? 0} items
                                                </div>
                                            </div>
                                            <div className="profiles-watchlist-list-item-action">
                                                <button
                                                    type="button"
                                                    className={`profiles-watchlist-add-btn${alreadyAdded ? " is-added" : ""}`}
                                                    onClick={() => handleSelectWatchlist(watchlist)}
                                                    disabled={isSubmitting || alreadyAdded}
                                                >
                                                    {isRowBusy ? (
                                                        <LoadingSpinnerImage small={true} />
                                                    ) : alreadyAdded ? (
                                                        "Added"
                                                    ) : (
                                                        "Add"
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {mode === "create" && (
                    <div>
                        <div style={{ marginBottom: "14px" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
                                Name *
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={isSubmitting}
                                style={{
                                    width: "100%",
                                    border: errorName ? "1px solid #e53e3e" : "1px solid #ccc",
                                    padding: "6px 8px",
                                }}
                            />
                            {errorName && (
                                <div style={{ color: "#e53e3e", fontSize: "12px", marginTop: "4px" }}>
                                    Name is required
                                </div>
                            )}
                        </div>
                        <div style={{ marginBottom: "14px" }}>
                            <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
                                Description
                            </label>
                            <textarea
                                rows={4}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={isSubmitting}
                                style={{ width: "100%", border: "1px solid #ccc", padding: "6px 8px" }}
                            />
                        </div>
                        <ActionButtonsRow isLoading={isSubmitting}>
                            <CancelButton onClick={onClose} />
                            <SaveButton onClick={handleCreateAndAdd}>Create & Add</SaveButton>
                        </ActionButtonsRow>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddToWatchlistPopup;
